import { ArrowRight, Users } from 'lucide-react';
import { ChartValueTable } from '@/components/molecules/org-admin/charts/ChartCard';
import { OrgAdminEmptyState } from '@/components/molecules/org-admin/OrgAdminStates';
import { Section } from '@/components/molecules/org-admin/OrgAdminSection';
import type { OrgAdminTab } from '@/components/molecules/org-admin/types';
import type { OrgOverview } from '@/services/org-admin/overview';
import type { PersonSpendRow } from '@/services/org-admin/analysis';
import { formatCurrencyUsd } from '../usageFormat';
import { CardRow, RANK_ROWS, formatCount, formatUnitCost } from './overviewLayout';

/**
 * "What is it costing?" — Overview's money band, and the second thing the screen says.
 *
 * WHY IT MOVED UP. Overview used to open with adoption, on the stated reasoning that this is the
 * order someone EVALUATING the platform asks their questions in. That reasoning is sound, for the
 * evaluator — who visits about once a quarter. The recurring visitor is the operator, who visits ten
 * times as often and needs to know what the bill is doing. For a landing tab, frequency wins.
 *
 * WHAT THIS BAND IS AND IS NOT. It PREVIEWS, it does not own. A few rows per ranking, then a link
 * into Usage, which holds the canonical per-member and per-feature tables. Overview owning full
 * copies of those tables is how the panel came to render each of them three times.
 *
 * Two cards that used to live here have moved to Usage with the question they answer:
 *
 *   - **Spend concentration** now sits beside the per-member table it summarises. A distribution
 *     shown on a screen that does not list the people it distributes over made the reader navigate
 *     to check it.
 *   - **The cumulative chart**, which was titled "Total spend" — a third figure on this panel
 *     carrying that phrase — now sits with the daily trend under "Is it growing?" on Usage, because
 *     the two are one question at two resolutions. `dev`'s closed-month fix (a chart of pure
 *     actuals must not claim a projection) travelled with it to `usage/TrendSection`.
 */
export interface CostSectionProps {
  data: OrgOverview | null;
  /** Already filtered and trimmed by the panel — this section renders, it does not rank. */
  topMembers: PersonSpendRow[];
  width: number | null;
  /**
   * First load, no data yet.
   *
   * The counts below are plain numbers with no card of their own to carry a skeleton, so without
   * this they rendered `0` — and "0 seats at their limit" is a reassuring, wrong answer to read
   * while the request is still in flight. From `dev`.
   */
  isLoading: boolean;
  onNavigateTab: (tab: OrgAdminTab) => void;
}

export function CostSection({
  data,
  topMembers,
  width,
  isLoading,
  onNavigateTab,
}: CostSectionProps) {
  return (
    <Section
      title="What is it costing?"
      action={
        <button
          type="button"
          onClick={() => onNavigateTab('usage')}
          className="oa-interactive -mr-2 inline-flex h-7 items-center gap-1 rounded-md px-2 text-[11px] font-medium text-muted-foreground hover:bg-muted/50 hover:text-foreground"
        >
          All usage &amp; spend
          <ArrowRight className="h-3 w-3" aria-hidden />
        </button>
      }
    >
      <CardRow width={width}>
        <section className="oa-panel flex min-w-0 flex-col" aria-labelledby="oa-limits-title">
          <header className="oa-toolbar py-2.5">
            <h3 id="oa-limits-title" className="oa-title truncate">
              Budget standing
            </h3>
            <p className="oa-meta mt-0.5">How capped seats are tracking this cycle.</p>
          </header>
          <div className="grid gap-4 p-[var(--oa-gutter)]">
            <div>
              {isLoading ? (
                <div className="oa-skeleton h-[26px] w-10" />
              ) : (
                <p
                  className={
                    data?.limits.atLimit
                      ? 'text-[26px] font-semibold leading-none tabular-nums text-warning-strong'
                      : 'text-[26px] font-semibold leading-none tabular-nums text-foreground'
                  }
                >
                  {data?.limits.atLimit ?? 0}
                </p>
              )}
              <p className="oa-meta mt-1">Seats already at their limit</p>
            </div>
            <div>
              {isLoading ? (
                <div className="oa-skeleton h-[26px] w-10" />
              ) : (
                <p className="text-[26px] font-semibold leading-none tabular-nums text-foreground">
                  {data?.limits.projectedToHit ?? 0}
                </p>
              )}
              <p className="oa-meta mt-1">Projected to hit their limit this cycle</p>
            </div>
            {!isLoading && !!data?.limits.noLimitSet && (
              // Stated rather than folded into the healthy count: a seat with no ceiling can never
              // hit one, and counting it as "under limit" hides that none was ever set.
              <p className="oa-meta">
                {data.limits.noLimitSet} of {data.limits.totalSeats} seats have no limit set.
              </p>
            )}
          </div>
        </section>

        <section className="oa-panel flex min-w-0 flex-col" aria-labelledby="oa-members-title">
          <header className="oa-toolbar flex items-center justify-between gap-3 py-2.5">
            <div className="min-w-0">
              <h3 id="oa-members-title" className="oa-title truncate">
                Top spenders
              </h3>
              <p className="oa-meta mt-0.5">Highest spend in the selected period.</p>
            </div>
            <button
              type="button"
              onClick={() => onNavigateTab('usage')}
              className="oa-interactive inline-flex h-7 shrink-0 items-center gap-1 rounded-md px-2 text-[11px] font-medium text-muted-foreground hover:bg-muted/50 hover:text-foreground"
            >
              See all
              <ArrowRight className="h-3 w-3" aria-hidden />
            </button>
          </header>
          {isLoading ? (
            <div className="flex flex-col gap-2 p-[var(--oa-gutter)]">
              {[0, 1, 2].map((row) => (
                <div key={row} className="oa-skeleton h-4 w-full" />
              ))}
            </div>
          ) : topMembers.length ? (
            <ChartValueTable
              caption="Members ranked by spend over the selected period"
              columns={[{ label: 'Member' }, { label: 'Spend', align: 'right' }]}
              rows={topMembers.map((person) => ({
                key: person.userId,
                cells: [person.label, formatCurrencyUsd(person.windowUsd)],
              }))}
            />
          ) : (
            <OrgAdminEmptyState
              icon={Users}
              title="No spend in this period"
              description="Nobody has spent anything over the selected period."
              className="min-h-[140px]"
            />
          )}
        </section>
      </CardRow>

      <div className="mt-3">
        <section className="oa-panel flex min-w-0 flex-col" aria-labelledby="oa-services-title">
          <header className="oa-toolbar flex items-center justify-between gap-3 py-2.5">
            <div className="min-w-0">
              <h3 id="oa-services-title" className="oa-title truncate">
                Most expensive features
              </h3>
              {/* Cost per run, not total cost. Total cost ranks the feature everyone uses; cost per
                  run ranks the feature that is expensive to use, and only the second leads to a
                  decision. */}
              <p className="oa-meta mt-0.5">What each feature costs per run.</p>
            </div>
            <button
              type="button"
              onClick={() => onNavigateTab('usage')}
              className="oa-interactive inline-flex h-7 shrink-0 items-center gap-1 rounded-md px-2 text-[11px] font-medium text-muted-foreground hover:bg-muted/50 hover:text-foreground"
            >
              See all
              <ArrowRight className="h-3 w-3" aria-hidden />
            </button>
          </header>
          {isLoading ? (
            <div className="flex flex-col gap-2 p-[var(--oa-gutter)]">
              {[0, 1, 2, 3].map((row) => (
                <div key={row} className="oa-skeleton h-4 w-full" />
              ))}
            </div>
          ) : (
            <ChartValueTable
              caption="Features by average cost per run"
              columns={[
                { label: 'Feature' },
                { label: 'Average cost per run', align: 'right' },
                { label: 'Runs', align: 'right' },
              ]}
              rows={(data?.serviceEconomy ?? []).slice(0, RANK_ROWS).map((row) => ({
                key: row.service,
                cells: [
                  row.label,
                  row.costPerRunUsd === null ? '—' : formatUnitCost(row.costPerRunUsd),
                  formatCount(row.runs),
                ],
              }))}
            />
          )}
        </section>
      </div>
    </Section>
  );
}
